import { ChatState } from '@/enums/ChatState';
import { IPosterDocument } from '@/interfaces/IPosterDocument';

import { sanitizeForSerialization } from './jsonapi-client';
import { ITag } from './Tag';
import { enrichThinProfile, IThinProfile } from './ThinProfile';

/** A downloadable file the lecture ships with (paper, slideshow). */
export interface IDownloadMaterial {
  url: string;
  filename: string;
  size: number;
}

export interface IThinLecture {
  id: string;
  type: 'thin_lectures';
  title: string;
  doi: string;
  video_doi: string;
  underline_doi: string;
  event_id: string;
  package: string;
  playlist: string;
  abstract: string;
  ai_summary: string;
  ai_insights: string;
  ai_questions_answers: string;
  synopsis: string;
  held_at: string | null;
  ends_at: string | null;
  published_at: string | null;
  library: boolean;
  visible: boolean;
  voting: boolean;
  poster_lecture: boolean;
  slug: string;
  original_poster_url: string;
  large_poster_url: string;
  medium_poster_url: string;
  small_poster_url: string;
  thumbnail_poster_url: string;
  preloader_poster_url: string;
  large_poster_document_thumbnail_url: string;
  medium_poster_document_thumbnail_url: string;
  small_poster_document_thumbnail_url: string;
  large_poster_document_thumbnail_extract_url: string;
  medium_poster_document_thumbnail_extract_url: string;
  small_poster_document_thumbnail_extract_url: string;
  video_started_count: number;
  comments_count: number;
  awards: string;
  highlighted_color: string;
  occurrence_match_color: string;
  gather_town_url: string;
  chat_state: ChatState;
  rocketchat_url: string;
  twilio_questions_channel: string;
  paper_link: string;
  paper_url: string;
  slideshow_url: string;
  // download materials, returned as attributes rather than relationships
  paper?: IDownloadMaterial | null;
  slideshow?: IDownloadMaterial | null;
  poster_document?: IPosterDocument | null;
  // Relationships (resolved by kitsu-core)
  sorted_profiles?: Array<IThinProfile>;
  position_sorted_profiles?: Array<IThinProfile>;
  tag?: ITag | null;
  // Drives the bookmark's saved state - a non-empty array means the lecture is on the agenda.
  agenda_lectures?: Array<{ id: string; type?: string }>;
  // Relationship linkage only (kitsu keeps { id, type } when not sideloaded) —
  // used to build the notification deep-link without fetching the session.
  // Linkage only unless the query includes it, in which case kitsu resolves the session's own
  // attributes too - the search cards need its name, and its url/poster_only to build the link.
  event_session?: { id: string; type?: string; name?: string; url?: string; poster_only?: boolean };
  // Computed (added after enrichment)
  poster_thumbnail_url?: string;
  link_slug?: string;
}

export function enrichThinLecture(lecture: IThinLecture): IThinLecture {
  const poster_thumbnail_url = lecture.poster_lecture
    ? lecture.medium_poster_document_thumbnail_url || lecture.medium_poster_document_thumbnail_extract_url
    : lecture.medium_poster_url;

  // Sanitize after enrichment to handle properties that may be undefined
  return sanitizeForSerialization({
    ...lecture,
    poster_thumbnail_url: poster_thumbnail_url || '',
    link_slug: `${lecture.id}-${lecture.slug}`,
    sorted_profiles: lecture.sorted_profiles?.map(enrichThinProfile) ?? [],
    position_sorted_profiles: lecture.position_sorted_profiles?.map(enrichThinProfile) ?? [],
    tag: lecture.tag ?? null,
  });
}
